import { FC, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  Avatar,
  Divider,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { Comment, createComment } from '../../services/university-service';
import { useTranslation } from 'react-i18next';

interface CommentsProps {
  comments: Comment[];
  contentId: number;
  onCommentAdded?: () => void;
}

const Comments: FC<CommentsProps> = ({ comments, contentId, onCommentAdded }) => {
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { t } = useTranslation();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;

    try {
      setIsSubmitting(true);
      await createComment({
        content: newComment.trim(),
        contentId,
        active: true,
      });

      toast({
        title: t('university.comments.success.sent.title'),
        description: t('university.comments.success.sent.description'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setNewComment('');
      onCommentAdded?.();
    } catch (error: any) {
      toast({
        title: t('university.comments.error.sending'),
        description: error.message || t('university.comments.error.try_again'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t('university.comments.time.now');
    if (diffInHours < 24) return t('university.comments.time.hours_ago', { hours: diffInHours, count: diffInHours });

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('university.comments.time.days_ago', { days: diffInDays, count: diffInDays });

    return date.toLocaleDateString();
  };

  const renderComment = (comment: Comment, isReply = false) => (
    <Box key={comment.id} ml={isReply ? 8 : 0} mb={4}>
      <HStack align="start" spacing={3}>
        <Avatar
          size="sm"
          src={comment.user?.name ? `https://ui-avatars.com/api/?name=${comment.user.name}&background=random` : undefined}
          name={comment.user?.name || t('university.comments.user.default')}
        />
        <Box flex={1}>
          <HStack justify="space-between" mb={1}>
            <Text fontWeight="medium" fontSize="sm" color={textColor}>
              {comment.user?.name || t('university.comments.user.default')}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {formatTimestamp(comment.createdAt)}
            </Text>
          </HStack>
          <Text fontSize="sm" color={textColor} mb={2}>
            {comment.content}
          </Text>
          {!isReply && (
            <Button size="xs" variant="ghost" colorScheme="blue">
              {t('university.comments.reply')}
            </Button>
          )}
        </Box>
      </HStack>
      {comment.replies && comment.replies.map(reply => renderComment(reply, true))}
    </Box>
  );

  return (
    <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
      <Text fontSize="lg" fontWeight="semibold" mb={4} color={textColor}>
        {t('university.comments.title', { count: comments.length })}
      </Text>
      <Text fontSize="sm" color="gray.500" mb={6}>
        {t('university.comments.description')}
      </Text>

      {/* New comment form */}
      <VStack spacing={3} align="stretch" mb={6}>
        <Textarea
          placeholder={t('university.comments.placeholder')}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          rows={3}
          bg={useColorModeValue('gray.50', 'gray.700')}
          resize="vertical"
        />
        <HStack justify="flex-end">
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleCommentSubmit}
            isDisabled={!newComment.trim() || isSubmitting}
            isLoading={isSubmitting}
            loadingText={t('university.comments.sending')}
          >
            {t('university.comments.comment')}
          </Button>
        </HStack>
      </VStack>

      <Divider mb={6} />

      {/* Comments list */}
      {comments.length > 0 ? (
        <VStack align="stretch" spacing={4}>
          {comments.map(comment => renderComment(comment))}
        </VStack>
      ) : (
        <Text color="gray.500" textAlign="center" py={8}>
          {t('university.comments.no_comments')}
        </Text>
      )}
    </Box>
  );
};

export default Comments; 